import {
  addDoc,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { useCallback, useMemo } from "react";
import { useCollection } from "react-firebase-hooks/firestore";
import { Ref } from "refs";

/**
 *
 * takes a `conversationId` which is an id of the form `${string}#${string}` and
 * references a document in the `conversations` firestore collection.
 */
export const useConversationMessages = (conversationId: string) => {
  const [messages, messagesLoading, messagesError] = useCollection(
    query(
      Ref("conversations/{conversationId}/messages", { conversationId }),
      orderBy("createdAt", "asc"),
    ),
  );

  const messageDocs = useMemo(
    () => messages?.docs.map((msg) => ({ ...msg.data(), id: msg.id })) ?? [],
    [messages],
  );

  /**
   *
   * takes a `message` and a `source` and adds a `message` doc to the given `conversationId` subcollection.
   */
  async function sendMessage({
    message,
    uid,
  }: {
    message: string;
    uid: string;
  }) {
    if (!message) {
      console.debug("message is empty");
      return;
    }

    try {
      await addDoc(
        Ref("conversations/{conversationId}/messages", { conversationId }),
        {
          // required author per firestore message security rules
          author: "system",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          message,
          // required source per firestore message security rules
          source: "Firestore",
          status: "pending",
          uid,
        },
      );
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * marks the current conversation as read up to date. (`unreadCount` is set to 0)
   */
  const markRead = useCallback(async () => {
    try {
      await updateDoc(
        Ref("conversations/{conversationId}", { conversationId }),
        { unreadCount: 0 },
      );
    } catch (err) {
      console.error("there was a problem setting messages as `read`", err);
    }
  }, [conversationId]);

  return {
    markRead,
    messages: messageDocs,
    messagesLoading,
    messagesError,
    sendMessage,
  };
};
